import React from 'react';
import classNames from 'classnames/bind';
import { Skeleton } from '@mantine/core';

import { Gateway } from 'types/gateway';

import { useGateway } from 'gateways/hooks/useGateway';
import { useCreateCameraDiscoveryRequest } from 'gateways/hooks/useCreateCameraDiscoveryRequest';

import { Box } from 'components/Box/Box';
import { DiscoveredAndLinkedGatewayCameras } from 'cameras/components/LinkGatewayCamera/DiscoveredAndLinkedGatewayCameras';
import { Field } from 'components/Field/Field';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Heading } from 'components/Heading/Heading';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';
import { Text } from 'components/Text/Text';

import styles from './DiscoverCameras.module.scss';

const c = classNames.bind(styles);

type DiscoverCamerasProps = {
  gateway?: Gateway;
};

export function DiscoverCameras({
  gateway: defaultGateway,
}: DiscoverCamerasProps) {
  const defaultGatewayId = defaultGateway?.id;

  const [gatewayId, setGatewayId] = React.useState(defaultGatewayId);
  const { data: gateway, isInitialLoading } = useGateway(gatewayId);

  const { data: discoveryRequest, mutate: createCameraDiscoveryRequest } =
    useCreateCameraDiscoveryRequest();

  React.useEffect(() => {
    // Trigger discover when add camera is selected from gateway detail
    if (
      !defaultGateway ||
      (discoveryRequest && discoveryRequest.gateway_id === defaultGateway.id)
    ) {
      return;
    }

    createCameraDiscoveryRequest({ gateway_id: defaultGateway.id });
  }, [createCameraDiscoveryRequest, defaultGateway, discoveryRequest]);

  function handleGatewayChange(gatewayId: string) {
    setGatewayId(gatewayId);

    createCameraDiscoveryRequest({ gateway_id: gatewayId });
  }

  return (
    <>
      {!defaultGateway && (
        <Field label="Select gateway">
          <GatewaySelect
            id="up-add-camera_gateway"
            onChange={(selected) =>
              selected && handleGatewayChange(selected.id)
            }
          />
        </Field>
      )}

      <div>
        {!gatewayId && (
          <Box className={c('no-selection-hint')}>
            <Text>Please select a gateway to continue.</Text>
          </Box>
        )}

        {!gateway && isInitialLoading && (
          <Skeleton w="100%" h="5rem" radius="12px" />
        )}

        {gateway && (
          <>
            {gateway.status === 'offline' ? (
              <Box className={c('box')}>
                <StatusIndicator
                  className={c('box-icon')}
                  status="offline"
                  size="small"
                />
                <Heading className={c('box-heading')} level="3">
                  The selected gateway is offline.
                </Heading>
                <Text className={c('box-text')} type="paragraph">
                  Camera discovery is unavailable. Please make sure your gateway
                  is connected to the internet or try adding a camera manually.
                </Text>
              </Box>
            ) : (
              <DiscoveredAndLinkedGatewayCameras
                gatewayId={gateway.id}
                discoveryRequest={discoveryRequest}
                discover={() =>
                  createCameraDiscoveryRequest({ gateway_id: gateway.id })
                }
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
